import { useCallback, useRef, useState } from "react";

import searchUIClient from "@/apis/searchUIClient";

/**
 * Builds the "review one cell tower at a time" queue for the WiFi-cellular
 * mismatch page.
 *
 * Same work the reviewer did by hand - group by cellular tower info, sort by
 * count descending, then filter the list down to one tower - but driven from
 * the advanced-search endpoint so whatever filters are active on the page still
 * apply. The grouped query projects only the columns listed in the
 * `cellularInfo` descriptor's aggregateColumnIdentifiers (cellId, net, mcc,
 * area), which is exactly the tower identity.
 */

const QUERY_OBJECT_TYPE = "WIFI_CELLULAR_MISMATCH";

/** Towers to queue up in one sitting. Beyond this the reviewer is better off narrowing the filters. */
const MAX_QUEUE_LENGTH = 500;

/** Mismatches resolved per tower - matches the cap the resolve dialog already applies. */
const MAX_MISMATCHES_PER_TOWER = 100;

export interface QueueFilterQuery {
  categoryId: string;
  filterKey: string;
  filterType: string;
  searchType: string;
  notFilter: boolean;
  filterValue: { searchText?: string };
}

export interface ReviewQueueEntry {
  mcc: number | null;
  net: number | null;
  area: number | null;
  cellId: number | null;
  mismatchCount: number;
}

interface GroupedRow {
  mcc?: number | string | null;
  net?: number | string | null;
  area?: number | string | null;
  cellId?: number | string | null;
  "cellId.count"?: number | string | null;
  "mcc.count"?: number | string | null;
}

interface IdRow {
  id: number;
  mcc?: number | string | null;
  net?: number | string | null;
  area?: number | string | null;
  cellId?: number | string | null;
}

const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/** Cell identity filters are rebuilt per tower, so any inherited ones must go. */
const TOWER_IDENTITY_FILTER_KEYS = ["cellId", "mcc", "net", "area"];

const towerIdentityFilter = (
  filterKey: string,
  value: number
): QueueFilterQuery => ({
  categoryId: "info",
  filterKey,
  filterType: "MULTI_STRING",
  searchType: "STATIC",
  notFilter: false,
  filterValue: { searchText: String(value) },
});

/** Page filters, minus the cell identity ones this queue sets itself. */
const baseFilters = (pageFilters: QueueFilterQuery[]): QueueFilterQuery[] =>
  (pageFilters || []).filter(
    (filter) => !TOWER_IDENTITY_FILTER_KEYS.includes(filter.filterKey)
  );

const filtersForTower = (
  pageFilters: QueueFilterQuery[],
  entry: ReviewQueueEntry
): QueueFilterQuery[] => {
  const filters = baseFilters(pageFilters);
  if (entry.cellId !== null)
    filters.push(towerIdentityFilter("cellId", entry.cellId));
  if (entry.mcc !== null) filters.push(towerIdentityFilter("mcc", entry.mcc));
  if (entry.net !== null) filters.push(towerIdentityFilter("net", entry.net));
  if (entry.area !== null)
    filters.push(towerIdentityFilter("area", entry.area));
  return filters;
};

/**
 * The identity filters match with LIKE, so "cell 1234" also matches "cell 12345".
 * Rows are re-checked here on the projected identity columns so a tower's queue
 * entry only ever resolves its own mismatches.
 */
const matchesTower = (row: IdRow, entry: ReviewQueueEntry): boolean =>
  toNumber(row.cellId) === entry.cellId &&
  toNumber(row.mcc) === entry.mcc &&
  toNumber(row.net) === entry.net &&
  toNumber(row.area) === entry.area;

export interface UseMismatchReviewQueue {
  queue: ReviewQueueEntry[];
  /** Index of the tower being reviewed; -1 when the queue is not running. */
  index: number;
  isLoadingQueue: boolean;
  isActive: boolean;
  /** Builds the queue and returns the first tower's mismatch ids, or null when nothing matches. */
  start: (pageFilters: QueueFilterQuery[]) => Promise<number[] | null>;
  /**
   * Advances past the current tower and returns the next one's mismatch ids.
   * Towers whose mismatches have since disappeared are skipped. Returns null at
   * the end of the queue.
   */
  advance: () => Promise<number[] | null>;
  stop: () => void;
}

export const useMismatchReviewQueue = (): UseMismatchReviewQueue => {
  const [queue, setQueue] = useState<ReviewQueueEntry[]>([]);
  const [index, setIndex] = useState(-1);
  const [isLoadingQueue, setIsLoadingQueue] = useState(false);
  const [isActive, setIsActive] = useState(false);

  // Mirrors of the state above - advance() reads them without waiting for a re-render
  const queueRef = useRef<ReviewQueueEntry[]>([]);
  const indexRef = useRef(-1);
  const pageFiltersRef = useRef<QueueFilterQuery[]>([]);

  const fetchQueue = useCallback(
    async (pageFilters: QueueFilterQuery[]): Promise<ReviewQueueEntry[]> => {
      const { data } = await searchUIClient.search({
        queryObjectType: QUERY_OBJECT_TYPE,
        searchQueryType: "BASIC",
        query: baseFilters(pageFilters),
        startIndex: 0,
        maxResults: MAX_QUEUE_LENGTH,
        requestedTableColumns: [
          // No aggregationType -> group by the tower identity columns
          { columnKey: "cellularInfo" },
          // Aggregated twin of the same column -> count per group, biggest first
          {
            columnKey: "cellularInfo",
            aggregationType: "COUNT",
            order: "DESC",
          },
        ],
      });

      const rows: GroupedRow[] = data?.data || [];
      return rows
        .map((row) => ({
          mcc: toNumber(row.mcc),
          net: toNumber(row.net),
          area: toNumber(row.area),
          cellId: toNumber(row.cellId),
          // The count lands under "<alias>.count" - one per aggregate column,
          // all carrying the same group size
          mismatchCount:
            toNumber(row["cellId.count"]) ?? toNumber(row["mcc.count"]) ?? 0,
        }))
        .filter((entry) => entry.cellId !== null)
        .sort((a, b) => b.mismatchCount - a.mismatchCount);
    },
    []
  );

  const fetchMismatchIds = useCallback(
    async (entry: ReviewQueueEntry): Promise<number[]> => {
      const { data } = await searchUIClient.search({
        queryObjectType: QUERY_OBJECT_TYPE,
        searchQueryType: "BASIC",
        query: filtersForTower(pageFiltersRef.current, entry),
        startIndex: 0,
        maxResults: MAX_MISMATCHES_PER_TOWER,
        // cellularInfo comes along so the LIKE matches can be verified exactly
        requestedTableColumns: [
          { columnKey: "id" },
          { columnKey: "cellularInfo" },
        ],
      });

      const rows: IdRow[] = data?.data || [];
      return rows
        .filter((row) => matchesTower(row, entry))
        .map((row) => row.id);
    },
    []
  );

  /** Walks forward from `from` until a tower still has mismatches to resolve. */
  const loadFrom = useCallback(
    async (from: number): Promise<number[] | null> => {
      for (let next = from; next < queueRef.current.length; next++) {
        const ids = await fetchMismatchIds(queueRef.current[next]);
        if (ids.length > 0) {
          indexRef.current = next;
          setIndex(next);
          return ids;
        }
      }
      indexRef.current = -1;
      setIndex(-1);
      setIsActive(false);
      return null;
    },
    [fetchMismatchIds]
  );

  const start = useCallback(
    async (pageFilters: QueueFilterQuery[]): Promise<number[] | null> => {
      setIsLoadingQueue(true);
      pageFiltersRef.current = pageFilters || [];
      try {
        const entries = await fetchQueue(pageFilters);
        queueRef.current = entries;
        setQueue(entries);
        if (entries.length === 0) {
          queueRef.current = [];
          indexRef.current = -1;
          setIndex(-1);
          setIsActive(false);
          return null;
        }
        setIsActive(true);
        return await loadFrom(0);
      } finally {
        setIsLoadingQueue(false);
      }
    },
    [fetchQueue, loadFrom]
  );

  const advance = useCallback(() => loadFrom(indexRef.current + 1), [loadFrom]);

  const stop = useCallback(() => {
    queueRef.current = [];
    indexRef.current = -1;
    pageFiltersRef.current = [];
    setQueue([]);
    setIndex(-1);
    setIsActive(false);
  }, []);

  return { queue, index, isLoadingQueue, isActive, start, advance, stop };
};

export default useMismatchReviewQueue;
